/**
 * A dataset, as the rest of BroadPaper sees it: one `DataSource` and the
 * `ReportData` that fits it.
 *
 * The shape is deliberately small, and it is the whole vocabulary a Cloud user
 * ever meets:
 *
 * ```
 * All rows        every record, for a table or a repeater
 * First row       the record's fields on their own, for a heading or a total
 * Totals          the sum of each numeric column
 * Number of rows  a count
 * ```
 *
 * Two of those need defending. **First row** exists because a spreadsheet is a
 * list and a document usually is not: an invoice's client name, its date and its
 * reference are one value each, and they have to come from somewhere. Taking
 * them from the first record is the rule a person would apply themselves, and
 * saying so in the label — "First row" — is what stops it being a surprise.
 *
 * **Totals** exists because the alternative is an expression. `sum(data.rows.amount)`
 * is a perfectly good answer for a developer and an impossible one for the
 * audience this product is for; a field called "Total of Amount" is the same
 * calculation with none of the syntax. It is computed here rather than in the
 * template so that the number in the document and the number in the review
 * screen cannot disagree.
 */
import { formatCurrency, formatDate, formatNumber, formatPercent, toDisplayString, type DataSource, type FieldSchema, type FormatContext, type ReportData } from "@broadpaper/core";
import type { Dataset, SourceColumn } from "./types.js";

/**
 * The data source id every Cloud dataset uses.
 *
 * Fixed rather than generated, and that is what makes a template portable
 * between datasets: a starter template binds to `data.first.clientName`, and
 * pointing it at a different upload is a change of mapping rather than a rewrite
 * of every expression in the document.
 */
export const DATASET_SOURCE_ID = "data";

const NUMERIC = new Set(["number", "currency", "percent"]);

export function dataSourceFor(dataset: Dataset, opts: { id?: string; label?: string } = {}): DataSource {
  const columns = dataset.columns.filter((c) => c.include);
  const fields = Object.fromEntries(columns.map((c) => [c.name, fieldSchema(c)]));
  const numeric = columns.filter((c) => NUMERIC.has(c.type));

  const schema: Record<string, FieldSchema> = {
    rows: {
      type: "array",
      label: "All rows",
      description: `Every row of ${dataset.label}. Use this for a table or a repeating section.`,
      itemSchema: { type: "object", fields }
    },
    first: {
      type: "object",
      label: "First row",
      description: "The values from the first row on their own. Use these for a heading, a reference or a date.",
      fields
    }
  };
  if (numeric.length) {
    schema.totals = {
      type: "object",
      label: "Totals",
      description: "Each numeric column added up across every row.",
      fields: Object.fromEntries(numeric.map((c) => [c.name, { ...fieldSchema(c), label: `Total ${lower(c.label)}` }]))
    };
  }
  schema.rowCount = { type: "number", label: "Number of rows", description: `How many rows ${dataset.label} has.` };

  return { id: opts.id ?? DATASET_SOURCE_ID, label: opts.label ?? dataset.label, icon: "table", schema };
}

export function fieldSchema(column: SourceColumn): FieldSchema {
  const schema: FieldSchema = { type: column.type, label: column.label };
  // `scaled` is an ingest marker meaning "the file wrote 12.5 for 12.5%", and
  // the value has already been divided by the coercion. It must not travel on
  // into the template as a display pattern.
  if (column.format && column.format !== "scaled") schema.format = column.format;
  if (column.currency) schema.currency = column.currency;
  const example = column.samples[0];
  if (example !== undefined) schema.example = example;
  return schema;
}

export function reportDataFor(dataset: Dataset, opts: { id?: string } = {}): ReportData {
  const columns = dataset.columns.filter((c) => c.include);
  const rows = dataset.rows.map((row) => Object.fromEntries(columns.map((c) => [c.name, row[c.name] ?? null])));
  const payload: Record<string, unknown> = {
    rows,
    first: rows[0] ?? Object.fromEntries(columns.map((c) => [c.name, null])),
    rowCount: rows.length
  };
  const numeric = columns.filter((c) => NUMERIC.has(c.type));
  if (numeric.length) payload.totals = Object.fromEntries(numeric.map((c) => [c.name, sum(rows, c.name)]));
  return { [opts.id ?? DATASET_SOURCE_ID]: payload };
}

/**
 * A sum that survives a column with gaps in it.
 *
 * Blanks contribute nothing rather than making the total null, which is what a
 * spreadsheet does and therefore what the user expects. A column with no numbers
 * at all totals zero, not NaN — a document showing NaN is worse than one showing
 * a zero somebody can see is wrong.
 */
function sum(rows: Array<Record<string, unknown>>, name: string): number {
  let total = 0;
  for (const row of rows) {
    const v = row[name];
    if (typeof v === "number" && Number.isFinite(v)) total += v;
  }
  // Money to the penny. Adding a few thousand floats gives 4012.8200000000006,
  // and rounding at the point of summing is cheaper than explaining it.
  return Math.round(total * 1e6) / 1e6;
}

/**
 * How a value will look in a document.
 *
 * The review screen's whole job: `0.125` → `12.5%`, `482310.4` → `£482,310.40`,
 * `2026-09-12` → `12 September 2026`. Formatted with the same functions the
 * renderer uses, so the preview cannot promise something the PDF does not do.
 */
export function previewValue(column: SourceColumn, value: unknown, ctx: Partial<FormatContext> = {}): string {
  if (value === null || value === undefined || value === "") return "";
  const format: FormatContext = { locale: "en-GB", currency: "GBP", ...ctx };
  switch (column.type) {
    case "currency":
      return formatCurrency(value, column.currency, undefined, format);
    case "percent":
      return formatPercent(value, undefined, format, false);
    case "number":
      return formatNumber(value, undefined, format);
    case "date":
      return formatDate(value, column.format, format, false);
    case "datetime":
      return formatDate(value, column.format, format, true);
    case "boolean":
      return value === true ? "Yes" : value === false ? "No" : "";
    default:
      return toDisplayString(value);
  }
}

/** `Portfolio Value` → `portfolio value`, for reading inside a sentence. */
function lower(label: string): string {
  return label.length > 1 && label.slice(1) === label.slice(1).toLowerCase() ? label[0]!.toLowerCase() + label.slice(1) : label;
}
